import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck, CalendarDays, MapPin, Car, Printer, ShieldCheck, Truck, Fuel, MessageCircle } from "lucide-react";
import JsBarcode from "jsbarcode";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const QUOTE_PRINT_CSS = `
@page { margin: 7mm; }
@media print {
  body { background: #ffffff !important; color: #000000 !important; }
  .no-print, footer, header { display: none !important; }
  .quote-shell { padding: 0 !important; margin: 0 !important; max-width: 100% !important; }
  .quote-doc { box-shadow: none !important; border: none !important; border-radius: 0 !important; padding: 4px 8px !important; font-size: 11px !important; }
  .quote-doc * { color: #000000 !important; }
  .quote-doc img { max-height: 56px !important; width: auto !important; }
  .quote-doc [data-testid="quote-barcode"] { max-height: none !important; max-width: none !important; height: 44px !important; width: 180px !important; }
  .quote-doc .text-sm { font-size: 11px !important; }
  .quote-doc .text-xs { font-size: 9.5px !important; }
  .quote-doc .mt-6, .quote-doc .mt-8 { margin-top: 10px !important; }
  .quote-doc .mb-6 { margin-bottom: 10px !important; }
  .quote-doc .pb-6 { padding-bottom: 8px !important; }
  .quote-doc .p-6, .quote-doc .sm\\:p-10 { padding: 10px !important; }
  .quote-doc .p-5 { padding: 8px !important; }
  .avoid-break { page-break-inside: avoid; }
}
`;

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

function Barcode({ value, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && value) JsBarcode(ref.current, value, { format: "CODE128", displayValue: true, fontSize: 13, height: 44, margin: 0, background: "transparent", lineColor: "#1f1f1f" });
  }, [value]);
  return <svg ref={ref} data-testid={testid} className="max-w-full" />;
}

export default function QuoteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/quote/${token}`)
      .then((r) => setQ(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Quote not found"));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quote/${token}/accept`);
      toast.success("Quote accepted — your booking is reserved");
      navigate(`/payments?booking=${data.booking_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept this quote");
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div data-testid="quote-error" className="pt-40 pb-24 px-6 text-center">
        <p className="font-display text-3xl text-stone-900">Quote unavailable</p>
        <p className="mt-3 text-sm font-ui text-muted-foreground">{error}</p>
        <p className="mt-1 text-sm font-ui text-muted-foreground">Questions? WhatsApp us at +1 (242) 813-1775.</p>
        <Link to="/fleet" className="inline-block mt-6 text-sm font-ui text-[#C71E2E] hover:underline">Browse our fleet</Link>
      </div>
    );
  }
  if (!q) return <div className="pt-40 pb-24 text-center text-muted-foreground font-ui">Loading your quote…</div>;

  const done = !!q.booking_id;
  const rows = [
    [`Rental — ${q.days} day${q.days === 1 ? "" : "s"} × ${fmt(q.daily_rate)}`, q.base_total],
    q.discount_amount ? [`Discount${q.coupon_code ? ` (${q.coupon_code})` : ""}`, -q.discount_amount] : null,
    ["Third-Party Liability Insurance — included", 0],
    q.processing_fee ? ["Processing fee (5%)", q.processing_fee] : null,
    q.vat_amount ? ["VAT (10%)", q.vat_amount] : null,
    q.airport_fee ? ["Airport drop-off fee", q.airport_fee] : null,
  ].filter(Boolean);

  const perks = [
    { icon: Truck, text: "Free hotel & villa delivery — anywhere on New Providence & Paradise Island" },
    { icon: Fuel, text: "Full-to-full fuel policy — return at the same level" },
    { icon: ShieldCheck, text: "Immaculately maintained, inspected & sanitized before every rental" },
    { icon: MessageCircle, text: "24/7 WhatsApp support — we reply in minutes" },
  ];

  return (
    <div data-testid="quote-page" className="quote-shell max-w-2xl mx-auto px-6 pt-32 pb-24">
      <style>{QUOTE_PRINT_CSS}</style>
      <div className="no-print mb-4 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-ui text-muted-foreground hover:text-[#C71E2E]"><ArrowLeft size={14} /> Back to site</Link>
        <Button data-testid="quote-print-button" variant="outline" onClick={() => window.print()} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui">
          <Printer size={14} className="mr-2" /> Print / Save PDF
        </Button>
      </div>
      <div className="quote-doc rounded-3xl border border-stone-200 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.06)] overflow-hidden">
        <div className="h-2 bg-gradient-to-r from-[#C71E2E] to-[#8A1420]" />
        <div className="p-6 sm:p-10">
          <div className="avoid-break grid grid-cols-12 gap-4 items-start border-b border-stone-200 pb-6 mb-6">
            <div className="col-span-7">
              <img src="/logo.png" alt="Easy Drive Car Rental" data-testid="quote-logo" className="h-20 w-auto object-contain" />
              <div className="mt-3 text-xs font-ui text-muted-foreground space-y-0.5">
                <p>West Bay Street · Nassau, Bahamas</p>
                <p>+1 (242) 813-1775 · bookings@easydrivecarrental.com</p>
                <p>www.easydrivecarrental.com</p>
              </div>
            </div>
            <div className="col-span-5 text-right">
              <p className="font-display text-3xl tracking-[0.1em] text-stone-900">QUOTE</p>
              <p data-testid="quote-ref" className="text-sm font-ui mt-1">{q.quote_ref}</p>
              <p className="text-xs font-ui text-muted-foreground mt-0.5">Valid until {new Date(q.expires_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
              <div className="mt-3 flex flex-col items-end">
                <Barcode value={q.quote_ref} testid="quote-barcode" />
                <p className="text-[9px] font-ui tracking-[0.2em] uppercase text-muted-foreground mt-1">Reference for office lookup</p>
              </div>
            </div>
          </div>

          <p className="text-sm font-ui text-stone-600">Prepared for <b className="text-stone-900">{q.customer_name}</b>{q.customer_email ? ` · ${q.customer_email}` : ""}{q.customer_phone ? ` · ${q.customer_phone}` : ""}</p>

          <div className="avoid-break mt-4 rounded-xl bg-stone-50 border border-stone-200 p-5 space-y-2 text-sm font-ui">
            <p className="flex items-center gap-2"><Car size={14} className="text-[#C71E2E]" /> <b>{q.vehicle_name}</b>{q.vehicle_seats ? ` · ${q.vehicle_seats} seats` : ""}{q.vehicle_luggage ? ` · fits ${q.vehicle_luggage} bags` : ""}</p>
            <p className="flex items-center gap-2"><CalendarDays size={14} className="text-[#C71E2E]" /> {q.start_date} → {q.end_date} ({q.days} day{q.days === 1 ? "" : "s"}){q.pickup_time ? ` · pickup ${q.pickup_time}` : ""}{q.dropoff_time ? ` · return ${q.dropoff_time}` : ""}</p>
            <p className="flex items-center gap-2"><MapPin size={14} className="text-[#C71E2E]" /> Pickup: {q.pickup_location} · Drop-off: {q.dropoff_location}</p>
          </div>

          <div className="avoid-break mt-6">
            {rows.map(([label, amt]) => (
              <div key={label} className="flex justify-between text-sm font-ui py-1.5 border-b border-stone-100">
                <span className="text-stone-600">{label}</span>
                <span className={amt < 0 ? "text-emerald-700" : "text-stone-900"}>{amt < 0 ? `−${fmt(-amt)}` : fmt(amt)}</span>
              </div>
            ))}
            <div className="flex justify-between items-baseline pt-3">
              <span className="font-ui font-semibold text-stone-900">Total</span>
              <span data-testid="quote-total" className="font-display text-2xl text-[#C71E2E]">{fmt(q.total)}</span>
            </div>
            <div className="flex justify-between text-xs font-ui text-muted-foreground mt-1">
              <span>Security deposit (refundable)</span>
              <span>{q.deposit_amount > 0 ? fmt(q.deposit_amount) : "Waived"}</span>
            </div>
          </div>

          <div className="avoid-break mt-6 rounded-xl border border-stone-200 p-5">
            <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">Included with every rental</p>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {perks.map(({ icon: Icon, text }) => (
                <p key={text} className="flex items-start gap-2 text-xs font-ui text-stone-600"><Icon size={13} className="text-[#C71E2E] mt-0.5 shrink-0" /> {text}</p>
              ))}
            </div>
          </div>

          <p className="avoid-break mt-6 text-[11px] font-ui text-muted-foreground leading-relaxed">
            This quote guarantees the quoted rate until the validity date above, subject to vehicle availability at acceptance. Pay online by card, PayPal or Zelle — or cash / bank wire at pickup. Cancellations within 24 hours of pickup are non-refundable. All charges are subject to 10% Bahamas VAT and a 5% processing fee as itemized. The security deposit is released once the vehicle is returned in good condition.
          </p>

          {done ? (
            <div data-testid="quote-already-accepted" className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center">
              <BadgeCheck className="mx-auto h-6 w-6 text-emerald-600" />
              <p className="mt-2 text-sm font-ui text-emerald-800">This quote was already converted to a booking.</p>
              <Button data-testid="quote-goto-payment" onClick={() => navigate(`/payments?booking=${q.booking_id}`)} className="no-print mt-3 rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui">Go to payment</Button>
            </div>
          ) : (
            <Button data-testid="quote-accept-button" onClick={accept} disabled={busy} className="no-print mt-8 w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui h-12 text-base">
              {busy ? "Reserving your booking…" : "Accept Quote & Pay"}
            </Button>
          )}

          <p className="mt-6 text-xs font-ui text-muted-foreground text-center">Questions or changes? WhatsApp +1 (242) 813-1775 · bookings@easydrivecarrental.com</p>
        </div>
      </div>
    </div>
  );
}
